import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    CollapsiblePartContentProps,
    CollapsibleContextType,
    CollapsiblePartKind
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const COLLAPSIBLE_PART_NAMES: Record<CollapsiblePartKind, string> = {
    content: "Content",
    trigger: "Trigger"
};

export const CollapsibleContext = createContext<CollapsibleContextType | undefined>(undefined);

export const useCollapsibleContext = ({
    part
}: {
    part: CollapsiblePartKind;
}): CollapsibleContextType | undefined => {
    const context = useContext(CollapsibleContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Collapsible.${COLLAPSIBLE_PART_NAMES[part]}: must be rendered inside <Collapsible>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withCollapsibleContext = <P extends object>(
    part: CollapsiblePartKind,
    Content: ComponentType<CollapsiblePartContentProps<P>>
): FC<P> & {
    collapsiblePart: CollapsiblePartKind;
} => {
    const CollapsiblePartBase: FC<P> = (props) => {
        const context = useCollapsibleContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    CollapsiblePartBase.displayName = `Collapsible.${COLLAPSIBLE_PART_NAMES[part]}`;

    return Object.assign(CollapsiblePartBase, {
        collapsiblePart: part
    });
};
